import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { AppDispatch, RootState } from '../../store/store';
import { getAlertSettings } from '../../rtk-apis/clinics/getAlertSettings';
import { updateAlertSettings } from '../../rtk-apis/clinics/updateAlertSettings';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* Per-clinic overdue-alert configuration (admin only): enable/disable, the
   "report unfinalized for X days" threshold, and the daily send hour
   (clinic local time, from the clinic address). Backed by GET/PUT /v2/clinic/alertSettings. */

const HOURS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${String(h).padStart(2, '0')}:00`,
}));

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent can refresh overdue data. */
  onSaved?: () => void;
};

const AlertSettingsDialog: React.FC<Props> = ({ open, onClose, onSaved }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { result, loading } = useSelector((s: RootState) => s.getAlertSettingsResponse);
  const { loading: saving } = useSelector((s: RootState) => s.updateAlertSettingsResponse);

  const [enabled, setEnabled] = useState(true);
  const [thresholdDays, setThresholdDays] = useState(3);
  const [alertHour, setAlertHour] = useState(8);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      dispatch(getAlertSettings());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (result) {
      setEnabled(result.overdueAlertsEnabled !== false);
      if (result.overdueThresholdDays != null) {
        setThresholdDays(result.overdueThresholdDays);
      }
      if (result.alertHour != null) {
        setAlertHour(result.alertHour);
      }
    }
  }, [result]);

  const handleSave = async () => {
    const days = Number(thresholdDays);
    if (!Number.isInteger(days) || days < 1 || days > 60) {
      setError('Threshold must be a whole number between 1 and 60 days.');
      return;
    }
    setError('');
    try {
      await dispatch(
        updateAlertSettings({
          overdueAlertsEnabled: enabled,
          overdueThresholdDays: days,
          alertHour,
        })
      ).unwrap();
      onSaved?.();
      onClose();
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to save alert settings.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Overdue report alerts</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={e => setEnabled(e.target.checked)}
                />
              }
              label="Send overdue report alerts to doctors"
            />
            <TextField
              label="Alert when unfinalized for (days)"
              type="number"
              size="small"
              value={thresholdDays}
              onChange={e => setThresholdDays(Number(e.target.value))}
              inputProps={{ min: 1, max: 60, step: 1 }}
              disabled={!enabled}
              fullWidth
            />
            <TextField
              label="Send alerts at (clinic local time)"
              select
              size="small"
              value={alertHour}
              onChange={e => setAlertHour(Number(e.target.value))}
              disabled={!enabled}
              fullWidth
            >
              {HOURS.map(h => (
                <MenuItem
                  key={h.value}
                  value={h.value}
                >
                  {h.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography sx={{ fontSize: 12, color: T.ink3 }}>
              Assigned doctors get one push notification per day for each report still unfinalized
              past the threshold. The overdue widgets on this dashboard use the same threshold.
            </Typography>
            {error && <Typography sx={{ fontSize: 12.5, color: T.red }}>{error}</Typography>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertSettingsDialog;
